import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import './medschools.css';

function MedSchools() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const iframeRef = useRef(null);

  // # Get iframe URL from environment variable
  const iframeUrl = process.env.REACT_APP_MEDSCHOOLS_IFRAME_URL || "https://wry-drill-0b1.notion.site/ebd/1c7db17e1b8e80288906e77febcf9c41";

  // # Load user theme preferences
  useEffect(() => {
    const loadThemePreference = async () => {
      try {
        if (user?.uid) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setIsDarkMode(userData.themes?.gen_theme === 'dark');
          }
        }
      } catch (error) {
        console.error('Error loading theme preference:', error);
      }
    };

    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Apply theme directly to iframe if exists
      if (iframeRef.current) {
        applyThemeToIframe(iframeRef.current, newTheme);
      }
      
      if (user?.uid) {
        await setDoc(doc(db, 'users', user.uid), {
          themes: { gen_theme: newTheme ? 'dark' : 'light' }
        }, { merge: true });
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    }
  };

  // # Function to apply theme directly to iframe
  const applyThemeToIframe = (iframe, darkMode) => {
    if (iframe) {
      try {
        // # Update iframe data-theme attribute
        iframe.setAttribute('data-theme', darkMode ? 'dark' : 'light');
        
        // # Update iframe style - remove filters, use proper CSS variables
        iframe.style.colorScheme = darkMode ? 'dark' : 'light';
        iframe.style.backgroundColor = darkMode ? 'var(--medschools-bg-primary)' : 'white';
        
        // # Add mobile viewport meta tag
        const meta = document.createElement('meta');
        meta.name = 'viewport';
        meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
        
        // # Attempt to inject theme styles into the iframe
        setTimeout(() => {
          try {
            if (iframe && iframe.contentDocument) {
              iframe.contentDocument.head.appendChild(meta);
              const style = document.createElement('style');
              if (darkMode) {
                // # Dark mode styles without filters
                style.textContent = `
                  /* # Dark mode base styles */
                  body, html {
                    background-color: var(--medschools-bg-primary) !important;
                    color-scheme: dark !important;
                    color: var(--medschools-text-primary) !important;
                    overflow-x: hidden !important;
                    width: 100% !important;
                    max-width: 100vw !important;
                  }
                  
                  /* # Dark mode text colors */
                  h1, h2, h3, h4, h5, h6, p, span, div {
                    color: var(--medschools-text-primary) !important;
                    word-wrap: break-word !important;
                    overflow-wrap: break-word !important;
                  }
                  
                  /* # Ensure content fits mobile screens */
                  img, video, iframe {
                    max-width: 100% !important;
                    height: auto !important;
                  }
                  
                  /* # Dark mode backgrounds */
                  .notion-page-content,
                  .notion-page-block {
                    background-color: var(--medschools-bg-primary) !important;
                    max-width: 100vw !important;
                    overflow-x: hidden !important;
                  }
                  
                  /* # Dark mode for block elements */
                  .notion-callout-block {
                    background-color: var(--medschools-bg-secondary) !important;
                    border-color: var(--medschools-card-border) !important;
                  }
                  
                  /* # Tables in dark mode */
                  .notion-collection-table {
                    background-color: var(--medschools-bg-primary) !important;
                    border-color: var(--medschools-card-border) !important;
                  }
                  
                  .notion-collection-table th,
                  .notion-collection-table td {
                    border-color: var(--medschools-card-border) !important;
                    color: var(--medschools-text-primary) !important;
                  }
                  
                  /* # Links in dark mode */
                  a, a:visited {
                    color: var(--medschools-link-color) !important;
                  }
                `;
              } else {
                // # Light mode styles with mobile optimizations
                style.textContent = `
                  body, html {
                    background-color: white !important;
                    color-scheme: light !important;
                    color: #2c3e50 !important;
                    overflow-x: hidden !important;
                    width: 100% !important;
                    max-width: 100vw !important;
                  }
                  
                  h1, h2, h3, h4, h5, h6, p, span, div {
                    color: #2c3e50 !important;
                    word-wrap: break-word !important;
                    overflow-wrap: break-word !important;
                  }
                  
                  /* # Ensure content fits mobile screens */
                  img, video, iframe {
                    max-width: 100% !important;
                    height: auto !important;
                  }
                  
                  /* # Light mode backgrounds */
                  .notion-page-content,
                  .notion-page-block {
                    background-color: white !important;
                    max-width: 100vw !important;
                    overflow-x: hidden !important;
                  }
                  
                  /* # Light mode for block elements */
                  .notion-callout-block {
                    background-color: #f8f9fa !important;
                    border-color: rgba(55, 53, 47, 0.1) !important;
                  }
                  
                  /* # Tables in light mode */
                  .notion-collection-table {
                    background-color: white !important;
                    border-color: rgba(55, 53, 47, 0.1) !important;
                  }
                  
                  a, a:visited {
                    color: var(--medschools-link-color) !important;
                  }
                `;
              }
              iframe.contentDocument.head.appendChild(style);
            }
          } catch (e) {
            // # Silently fail if cross-origin issues prevent access
          }
        }, 1000);
      } catch (e) {
        // # Silently fail if cross-origin issues prevent access
      }
    }
  };

  // # Handle iframe load events
  const handleIframeLoad = () => {
    if (iframeRef.current) {
      try {
        // # Apply theme directly to iframe
        applyThemeToIframe(iframeRef.current, isDarkMode);
        
        // # Try to access iframe content to inject error suppression and styling
        const style = document.createElement('style');
        style.textContent = `
          /* # Hide elements that might expose URLs */
          .notion-overlay-container, 
          .notion-login-prompt,
          .notion-help-button,
          .notion-cursor-listener,
          .notion-topbar,
          .notion-selectable[data-block-id],
          .notion-frame > div[style*="fixed"],
          .notion-frame > div[style*="absolute"],
          .notion-link-token,
          .notion-collection-card__content,
          .notion-header__icon-wrapper {
            display: none !important;
          }
          
          /* # Force theme colors and prevent overflow */
          body, html {
            background-color: ${isDarkMode ? 'var(--medschools-bg-primary)' : 'white'} !important;
            color-scheme: ${isDarkMode ? 'dark' : 'light'} !important;
            color: ${isDarkMode ? 'var(--medschools-text-primary)' : '#2c3e50'} !important;
            overflow-x: hidden !important;
            width: 100% !important;
            max-width: 100vw !important;
          }
        `;
        
        // # Attempt to inject the style into the iframe
        setTimeout(() => {
          try {
            if (iframeRef.current && iframeRef.current.contentDocument) {
              iframeRef.current.contentDocument.head.appendChild(style);
            }
          } catch (e) {
            // # Silently fail if cross-origin issues prevent access
          }
        }, 1000);
      } catch (e) {
        // # Silently fail if cross-origin issues prevent access
      }
    }
  };

  return (
    <div className={`md_scl_container ${isDarkMode ? 'md_scl_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      <main className="md_scl_content">
        {/* # Error suppressor element */}
        <div className="md_scl_error-suppressor" aria-hidden="true"></div>
        
        <div className="md_scl_iframe-container">
          <iframe
            ref={iframeRef}
            src={iframeUrl}
            title="Medical Schools Content"
            className={isDarkMode ? 'md_scl_dark-mode-iframe' : 'md_scl_light-mode-iframe'}
            allowFullScreen
            referrerPolicy="no-referrer"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            data-theme={isDarkMode ? 'dark' : 'light'}
            style={{
              colorScheme: isDarkMode ? 'dark' : 'light',
              backgroundColor: isDarkMode ? 'var(--medschools-bg-primary)' : 'white'
            }}
            loading="lazy"
            importance="high"
            onLoad={handleIframeLoad}
          />
        </div>
      </main>
    </div>
  );
}

export default MedSchools;
